import React, { useEffect, useMemo, useState } from 'react';
import { useAI } from '../../context/AIContext';
import AIQuickPrompts from './AIQuickPrompts';
import AIInputBox from './AIInputBox';
import AIReviewLoadingComposer from './AIReviewLoadingComposer';

const AIChatComposer = ({
  chat,
  selectedElement,
  addEphemeralMessage,
  removeEphemeralMessage,
  draftValue,
  onDraftChange,
  onFocusInput,
  textareaRef,
  messageCount,
}) => {
  const { sending, startReviewChat, endReviewChat } = useAI();
  const isReviewMode = chat?.chat_type === 'site_review';
  const [isWritingMessage, setIsWritingMessage] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const totalMessages = useMemo(() => {
    if (typeof messageCount === 'number') {
      return messageCount;
    }
    return Array.isArray(chat?.messages) ? chat.messages.length : 0;
  }, [chat?.messages, messageCount]);
  const showStandardPrompts = totalMessages === 0 || isWritingMessage;

  useEffect(() => {
    setIsWritingMessage(false);
  }, [chat?.id]);

  // Reset reviewLoading when the chat changes (e.g. switching tabs)
  useEffect(() => {
    setReviewLoading(false);
  }, [chat?.id]);

  const handleSelectPrompt = (prompt) => {
    onDraftChange?.(prompt || '');
    onFocusInput?.();
  };

  const handleActivateReviewMode = async () => {
    if (!chat?.id || sending) return;
    setReviewLoading(true);
    try {
      await startReviewChat(chat.id);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleDeactivateReviewMode = async () => {
    if (!chat?.id || sending) return;
    await endReviewChat(chat.id);
  };

  // Show loading indicator while AI review is initialising
  if (reviewLoading) {
    return (
      <div className="flex flex-col shrink-0">
        <AIReviewLoadingComposer />
      </div>
    );
  }

  return (
    <div className="flex flex-col shrink-0">
      <AIQuickPrompts
        disabled={!chat?.id || sending}
        isReviewMode={isReviewMode}
        showStandardPrompts={showStandardPrompts}
        onSelectPrompt={handleSelectPrompt}
        onActivateReviewMode={handleActivateReviewMode}
        onDeactivateReviewMode={handleDeactivateReviewMode}
      />
      <AIInputBox
        chatId={chat?.id}
        chatType={chat?.chat_type}
        isReviewMode={isReviewMode}
        selectedElement={selectedElement}
        addEphemeralMessage={addEphemeralMessage}
        removeEphemeralMessage={removeEphemeralMessage}
        enableEphemeralWarnings
        draftValue={draftValue}
        onDraftChange={onDraftChange}
        textareaRef={textareaRef}
        onTextareaFocus={() => setIsWritingMessage(true)}
        onTextareaBlur={() => setIsWritingMessage(false)}
      />
    </div>
  );
};

export default AIChatComposer;
